// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import {
  init,
  makeFetchTransport,
  makeMultiplexedTransport,
  moduleMetadataIntegration,
  reactRouterV6BrowserTracingIntegration,
} from '@sentry/react';

import { name, version } from '../../package.json';
import { scrubBreadcrumb, scrubEvent } from '../shared/lib/sentryScrub';

const { VITE_APP_ENV, VITE_SENTRY_DSN, MODE } = import.meta.env;

const MICROFRONTEND_DSNS = 'MICROFRONTEND_DSNS';

const parseSampleRate = (value: string | undefined, fallback: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1
    ? parsed
    : fallback;
};

const normalizeEnvironment = (value: string | undefined) => {
  if (value === 'prod') return 'production';
  return value;
};

const transport = makeMultiplexedTransport(makeFetchTransport, (args) => {
  const event = args.getEvent();
  return event?.extra?.[MICROFRONTEND_DSNS] || [];
});

const initSentry = () => {
  if (!VITE_APP_ENV || VITE_APP_ENV === 'local' || MODE === 'development') {
    // eslint-disable-next-line no-console
    console.warn(`[Sentry]: Won't capture errors in "${MODE}" Vite's mode.`);
    return;
  }

  if (!VITE_SENTRY_DSN) {
    // eslint-disable-next-line no-console
    console.warn('[Sentry]: VITE_SENTRY_DSN is not set.');
    return;
  }

  const sentryEnvironment = normalizeEnvironment(VITE_APP_ENV);

  init({
    dsn: VITE_SENTRY_DSN,
    environment: sentryEnvironment,
    release: `${name}@${version}`,
    transport,
    integrations: [
      reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      moduleMetadataIntegration(),
    ],
    tracesSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      0.05
    ),
    beforeSend: (event) => {
      const frames = event?.exception?.values?.[0]?.stacktrace?.frames;
      if (frames) {
        const framesWithMetadata = frames.filter((f) => f.module_metadata);
        const microfrontendDsns = framesWithMetadata
          .map((frame) => frame.module_metadata.dsn)
          .filter((dsn, index, array) => array.indexOf(dsn) === index);

        if (microfrontendDsns.length) {
          // eslint-disable-next-line no-param-reassign
          event.extra = {
            ...event.extra,
            [MICROFRONTEND_DSNS]: microfrontendDsns,
            _debug_multiplexing: 'active', // маркер для отладки
            _debug_frames_count: framesWithMetadata.length,
          };
        }
      }

      return scrubEvent(event as Record<string, unknown>) as typeof event | null;
    },
    beforeBreadcrumb: (breadcrumb) =>
      scrubBreadcrumb(breadcrumb as Record<string, unknown>) as typeof breadcrumb | null,
  });
};

initSentry();
